import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ComponentsModule } from '@app/shared/components/components.module';

import { CoreModule } from '@core/core.module';
import { ConfigService } from '@core/config.service';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { SeoModule } from '@wdpr/ra-angular-seo-metadata';
import { GEO_LOCATION_CONFIG, GeolocationService } from './services/geolocation/geolocation.service';
import { HomepageAnalyticsService } from '@app/services/analytics/analytics.service';

import { HomepageModule } from './features/homepage/dcl/homepage.module';
import { CookieService } from 'ngx-cookie-service';
import { LocaleService } from '@app/services/locale/locale.service';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { FeatureToggleModule } from '@wdpr/ra-angular-feature-toggle';
import { AuthHttpInterceptor } from '@core/interceptors/auth-interceptor';
import { TravelAgentInterceptor } from '@core/interceptors/travel-agent.interceptor';
import { ErrorLogHttpInterceptor } from '@core/interceptors/error-log.interceptor';
import { UniversalResourcesDirective } from './shared/directives/universal-resources.directive';

import { RaInjectOneidAuthTokenModule } from '@wdpr/ra-angular-oneid-auth-token-injector';
import { ContextualFilterService } from '@dcl/dcl-ui-components-library';
import { ResourceBundleService } from "@app/services/resource-bundle/resource-bundle.service";

@NgModule({
    declarations: [AppComponent, UniversalResourcesDirective],
    exports: [AppComponent, ComponentsModule],
    imports: [
        FeatureToggleModule,
        BrowserAnimationsModule,
        BrowserModule,
        ComponentsModule,
        HttpClientModule,
        CoreModule,
        AppRoutingModule,
        SeoModule,
        HomepageModule,
        RaInjectOneidAuthTokenModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
    ],
    providers: [
        ContextualFilterService,
        ResourceBundleService,
        TravelAgentInterceptor,
        AuthHttpInterceptor,
        ErrorLogHttpInterceptor,
        LocaleService,
        GeolocationService,
        HomepageAnalyticsService,
        CookieService,
        {
            provide: 'SEO_OPTIONS',
            useValue: {
                customSeoRetrieval: true,
                includeMetaTag: false
            },
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (config: ConfigService) => () => {
                return config.load().then((result: any) => {
                    return result;
                }).catch((err) => {
                    return err;
                });
            },
            deps: [ConfigService],
            multi: true,
        },
        {
            provide: 'PAGE_KEY_URL_ENDPOINT',
            useValue: '/wdpr-web-dcl-homepage/page-key',
        },
        {
            provide: 'PAGE_KEY_TEMPLATE',
            useValue: ['siteId', 'path'],
        },
        {
            provide: GEO_LOCATION_CONFIG,
            useValue: {
                URL: '/wdpr-web-dcl-homepage/geocode/',
                COOKIE_AKA: 'geolocation_aka_jar',
                COOKIE_JAR: 'GEOLOCATION_jar',
                ISO_COUNTRY_MAP: {
                    AR: 'argentina',
                    AU: 'australia',
                    BR: 'brazil',
                    CA: 'canada',
                    CL: 'chile',
                    CO: 'colombia',
                    DE: 'germany',
                    ES: 'spain',
                    EU: 'europe',
                    FR: 'france',
                    GB: 'united+kingdom',
                    ID: 'indonesia',
                    IN: 'india',
                    IT: 'italy',
                    MX: 'mexico',
                    MY: 'malaysia',
                    NZ: 'new+zealand',
                    PE: 'peru',
                    PH: 'philippines',
                    SG: 'singapore',
                    US: 'united+states',
                },
                REGION_MAP: {
                    CA: 'california',
                    FL: 'florida',
                },
            },
        },
        {
            provide: 'LOGASAURUS_URL',
            useValue: '/wdpr-web-dcl-homepage/error',
        },
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent],
})
export class AppModule {}

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(
        http,
        '/wdpr-web-dcl-homepage/i18n/quick-quote/',
        '.json'
    );
}
